/**
 * Fee Schedule Settings Component
 *
 * Allows organization admins to configure baseline CPT rates for billing.
 * Pre-populated with common behavioral health codes, with ability to add custom codes.
 */

import { useCallback, useEffect, useState } from 'react';
import { CPT_CODES, type CPTCodeOption } from '@components/ui/forms/CPTCodePicker';
import {
  useDeleteBaselineFeeScheduleMutation,
  useGetBaselineFeeScheduleQuery,
  useUpdateBaselineFeeScheduleMutation,
} from '@features/billing/store/api';
import { AttachMoney, Delete, Upload } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { FeeScheduleUploadModal } from './FeeScheduleUploadModal';

// Primary codes (exclude add-ons) for pre-population
const PRIMARY_CPT_CODES = CPT_CODES.filter(code => code.category !== 'Add-on');

interface FeeScheduleRow {
  cpt_code: string;
  description: string;
  charge_cents: number | null;
  id?: string;
  category?: string;
}

export function FeeScheduleSection() {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: feeScheduleData,
    isLoading,
    refetch,
  } = useGetBaselineFeeScheduleQuery();

  const [updateFeeSchedule, { isLoading: isUpdating }] =
    useUpdateBaselineFeeScheduleMutation();

  const [deleteFeeSchedule, { isLoading: isDeleting }] =
    useDeleteBaselineFeeScheduleMutation();

  const [rates, setRates] = useState<FeeScheduleRow[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedCode, setSelectedCode] = useState<CPTCodeOption | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Initialize rates from API data and merge with primary CPT codes
  useEffect(() => {
    if (feeScheduleData?.rates) {
      const existingRates = new Map(
        feeScheduleData.rates.map((r: any) => [r.cpt_code, r])
      );

      // Map primary codes with their existing rates
      const mergedRates: FeeScheduleRow[] = PRIMARY_CPT_CODES.map(cptCode => {
        const existing = existingRates.get(cptCode.cpt);
        return {
          cpt_code: cptCode.cpt,
          description: cptCode.description,
          category: cptCode.category,
          charge_cents: existing?.charge_cents ?? null,
          id: existing?.id,
        };
      });

      // Add any existing rates that aren't in the primary list
      feeScheduleData.rates.forEach((rate: any) => {
        if (!PRIMARY_CPT_CODES.some(c => c.cpt === rate.cpt_code)) {
          // Try to find description from full CPT_CODES list
          const cptInfo = CPT_CODES.find(c => c.cpt === rate.cpt_code);
          mergedRates.push({
            cpt_code: rate.cpt_code,
            description: cptInfo?.description ?? `CPT ${rate.cpt_code}`,
            category: cptInfo?.category,
            charge_cents: rate.charge_cents ?? null,
            id: rate.id ?? undefined,
          });
        }
      });

      setRates(mergedRates);
    } else {
      // Initialize with primary codes if no data
      setRates(
        PRIMARY_CPT_CODES.map(c => ({
          cpt_code: c.cpt,
          description: c.description,
          category: c.category,
          charge_cents: null,
        }))
      );
    }
  }, [feeScheduleData]);

  // Convert cents to dollar display
  const displayAmount = (cents: number | null): string => {
    if (cents === null) return '';
    return (cents / 100).toFixed(2);
  };

  // Convert dollar input to cents
  const toCents = (dollars: string): number | null => {
    const trimmed = dollars.trim();
    if (!trimmed) return null;
    const parsed = parseFloat(trimmed);
    if (isNaN(parsed)) return null;
    return Math.round(parsed * 100);
  };

  const handleAmountChange = useCallback(
    (cptCode: string, value: string) => {
      setRates(prev =>
        prev.map(r =>
          r.cpt_code === cptCode
            ? { ...r, charge_cents: toCents(value) }
            : r
        )
      );
      setHasChanges(true);
    },
    []
  );

  const handleAddCode = useCallback(
    (code: CPTCodeOption | null) => {
      if (!code) return;
      
      // Check if code already exists
      if (rates.some(r => r.cpt_code === code.cpt)) {
        enqueueSnackbar(`CPT ${code.cpt} is already in the list`, { variant: 'info' });
        return;
      }

      setRates(prev => [
        ...prev,
        {
          cpt_code: code.cpt,
          description: code.description,
          category: code.category,
          charge_cents: null,
        },
      ]);
      setSelectedCode(null);
      setHasChanges(true);
    },
    [rates, enqueueSnackbar]
  );

  const handleRemoveCode = useCallback((cptCode: string) => {
    setRates(prev => prev.filter(r => r.cpt_code !== cptCode));
    setHasChanges(true);
  }, []);

  const handleDeleteRate = useCallback(
    async (cptCode: string) => {
      try {
        await deleteFeeSchedule({ codeId: cptCode }).unwrap();
        enqueueSnackbar(`Rate for CPT ${cptCode} deleted successfully`, {
          variant: 'success',
        });
        refetch();
      } catch (error: any) {
        enqueueSnackbar(
          error?.data?.message || `Failed to delete rate for CPT ${cptCode}`,
          { variant: 'error' }
        );
      }
    },
    [deleteFeeSchedule, enqueueSnackbar, refetch]
  );

  const handleSave = useCallback(async () => {
    // Filter out rates with no amount set
    const ratesToSave = rates
      .filter(r => r.charge_cents !== null && r.cpt_code.trim() !== '')
      .map(r => ({
        cpt_code: r.cpt_code.trim(),
        charge_cents: r.charge_cents!,
      }));

    if (ratesToSave.length === 0) {
      enqueueSnackbar('Please enter at least one rate', { variant: 'warning' });
      return;
    }

    try {
      const result = await updateFeeSchedule({
        baselineRatesUpsertRequest: {
          rates: ratesToSave,
        },
      }).unwrap();

      enqueueSnackbar(
        `Fee schedule updated: ${result.created_count} created, ${result.updated_count} updated`,
        { variant: 'success' }
      );
      setHasChanges(false);
      refetch();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || 'Failed to update fee schedule',
        { variant: 'error' }
      );
    }
  }, [rates, updateFeeSchedule, enqueueSnackbar, refetch]);

  if (isLoading) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Loading fee schedule...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AttachMoney color="primary" />
          <Typography variant="h6" component="h2">
            Fee Schedule
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Set baseline rates for common CPT codes. These rates will be used when creating claims.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Enter charges in dollars (e.g., 150.00). Changes will be saved to your organization's baseline fee schedule.
        </Alert>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>CPT Code</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Charge Amount ($)</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rates.map((rate, index) => (
                <TableRow key={rate.cpt_code || `new-${index}`}>
                  <TableCell>
                    <Typography fontFamily="monospace">{rate.cpt_code}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{rate.description}</Typography>
                      {rate.category && (
                        <Typography variant="caption" color="text.secondary">
                          {rate.category}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      value={displayAmount(rate.charge_cents)}
                      onChange={e => handleAmountChange(rate.cpt_code, e.target.value)}
                      size="small"
                      placeholder="150.00"
                      type="number"
                      inputProps={{
                        step: '0.01',
                        min: '0',
                      }}
                      sx={{ width: '120px' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {rate.id ? (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRate(rate.cpt_code)}
                        disabled={isDeleting}
                        color="error"
                        title="Delete from server"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    ) : !PRIMARY_CPT_CODES.some(c => c.cpt === rate.cpt_code) ? (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveCode(rate.cpt_code)}
                        color="error"
                        title="Remove from list"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'flex-start' }}>
          <Autocomplete
            value={selectedCode}
            onChange={(_event, newValue) => handleAddCode(newValue)}
            options={CPT_CODES}
            groupBy={option => option.category}
            getOptionLabel={option => `${option.cpt} - ${option.description}`}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" fontFamily="monospace">
                    {option.cpt}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Add CPT Code"
                placeholder="Search CPT codes..."
                size="small"
              />
            )}
            sx={{ width: 400 }}
            ListboxProps={{
              sx: { maxHeight: 300 },
            }}
          />

          <Box sx={{ flex: 1 }} />

          <Button
            onClick={() => setUploadModalOpen(true)}
            variant="outlined"
            startIcon={<Upload />}
          >
            Upload PDF
          </Button>

          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!hasChanges || isUpdating}
            sx={{ minWidth: '120px' }}
          >
            {isUpdating ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </Box>

        {isUpdating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Updating fee schedule...
            </Typography>
          </Box>
        )}
      </CardContent>

      <FeeScheduleUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={() => {
          setUploadModalOpen(false);
          refetch();
          enqueueSnackbar('Fee schedule imported successfully', { variant: 'success' });
        }}
      />
    </Card>
  );
}
